// mobile.jsx — mobile-first chrome: bottom tab bar + daily home dashboard.
// Components are global (classic-script scope) so app.jsx can mount them.
const { useState: useStateM } = React;

// ── Bottom tab bar (replaces the hamburger as primary nav on phones) ─────────
function MobileTabBar({ route, go }) {
  const {topicsVisible} = window.useSiteSettings();
  const items = [
    ["home", "Home", "compass"],
    ["library", "Library", "book"],
    // The reader sits in the middle of the bar, where the thumb lands.
    ["reader", "Reader", "text"],
    ["explore", "Map", "graph"],
    ["research", "Research", "layers"],
    ["topics", "Topics", "path"],
  ];
  const active = route;
  const bar = (
    <nav className="mtabs" role="tablist" aria-label="Primary">
      {items.filter(([k]) => k !== "topics" || topicsVisible).map(([k, label, ic]) => (
        <button key={k} className={"mtab" + (active === k ? " on" : "")}
          role="tab" aria-selected={active === k} onClick={() => go(k)}>
          <span className="mtab-ic"><Icon d={ic} size={22} /></span>
          <span className="mtab-l">{label}</span>
        </button>
      ))}
    </nav>
  );
  // Keep fixed mobile chrome outside the application tree. Mobile Safari can
  // otherwise position a fixed descendant against an animated/transformed
  // ancestor, leaving the tab bar stranded halfway down a long home page.
  return typeof document === "undefined" ? bar : ReactDOM.createPortal(bar, document.body);
}

function mobileTopicConceptId(tp) {
  return (tp && (tp.slug || tp.id || tp.conceptId)) || "";
}

// ── Mobile daily home — a dashboard you open every day ───────────────────────
function MobileHome({ onOpenSicha, onOpenTopic, onOpenExplore, onOpenResearch, onOpenLibrary, direction = "beis", onDirection }) {
  const {topicsVisible} = window.useSiteSettings();
  const S = window.SICHA, T = window.TODAY, sources = window.SOURCES;
  const weekly = window.WEEKLY_FEATURE || {};
  const featured = weekly.ref ? { titleHe: weekly.heTitle, titleEn: weekly.title, subtitleEn: weekly.caption || weekly.parsha } : S;
  const graphState = window.useFullSichaPreview(weekly.ref || S?._liveTargetRef || S?._liveRef);
  const featuredSources = graphState.sources;
  const openFeatured = () => {
    if (!weekly.ref || !window.loadSicha) return onOpenSicha();
    // Load first so the route callback can simply switch to the reader while
    // preserving the exact date-selected weekly text on mobile.
    window.loadSicha(weekly.ref, { recordActivity: false }).then((loaded) => {
      if (loaded) onOpenSicha(weekly.ref);
    });
  };
  // match the desktop hero: cascade eyebrow → headline → sub (step 110, dur 760, y 20)
  const heroRef = window.useStagger ? window.useStagger({ y: 20, step: 110, dur: 760 }) : null;
  // the daily content below follows, entering ~260ms in like the desktop graph
  const ref = window.useStagger
    ? window.useStagger({ y: 16, step: 72, dur: 620, delay: 260,
        selector: ":scope > .mh-search, :scope > .mh-startq, :scope > .mh-daily-label, :scope > .mh-hero, :scope > .mh-duo, :scope > .mh-sec" })
    : null;
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  return (
    <div className="mhome" ref={ref} style={{ background: "var(--hero-grad)" }}>
      {/* editorial hero — the brand moment, mirrored from desktop */}
      <header className="mh-hero-ed" ref={heroRef}>
        <div className="mh-brandlock">

          <span><b>Mayanos <em lang="he" dir="rtl" className="heb">מעיינות</em></b><small>A living library of Chassidus</small></span>
        </div>
        <div className="mh-sponsorship" role="note">
          <span>{window.MAYANOS_SPONSORSHIP?.label || "Today's learning is sponsored"}</span>
          <strong lang="he" dir="rtl" className="heb">{window.MAYANOS_SPONSORSHIP?.compact || "לע״נ יעקב קלמן בן אלטר יהושע הכהן"}</strong>
        </div>
        <h1 className="mh-ed-title serif">Learn the text. <em>Follow its sources.</em></h1>
        <p className="mh-ed-sub">Read Chassidus, follow its sources, and build a source sheet.</p>
      </header>

      <button className="mh-library-cta library-entry-cta" onClick={onOpenLibrary}>
        <span><Icon d="book" size={19} /><b>Open the library</b></span>
        <span className="library-entry-arrow"><Icon d="chev" size={17} /></span>
      </button>

      <section className="mh-pathways" aria-label="Ways to use Mayanos">
        <button onClick={onOpenLibrary}>
          <span><Icon d="book" size={18} /></span>
          <b>Learn</b>
          <small>Open a text</small>
        </button>
        <button onClick={onOpenExplore}>
          <span><Icon d="graph" size={18} /></span>
          <b>Trace</b>
          <small>Follow sources</small>
        </button>
        <button onClick={onOpenResearch}>
          <span><Icon d="layers" size={18} /></span>
          <b>Build</b>
          <small>Create a source sheet</small>
        </button>
      </section>

      {/* daily strip */}
      <div className="mh-daily-label"><span>{greet}</span><span lang="he" dir="rtl" className="heb">{T.hayomyom.date} · {T.parsha}</span></div>

      {/* featured live text */}
      <button className="mh-hero" onClick={openFeatured}>
        <div className="mh-hero-tag"><span className="dot" style={{ background: "var(--gold)" }} /> {weekly.kind === "parsha" ? "This week's reading" : "Today's reading"}</div>
        <div lang="he" dir="rtl" className="mh-hero-he heb">{featured.titleHe}</div>
        <div className="mh-hero-en serif">{featured.titleEn}</div>
        <p className="mh-hero-sub">{featured.subtitleEn}</p>
        <div className="mh-hero-meta">
              <span className="chip"><Icon d="graph" size={12} /> {weekly.ref ? (weekly.kind === "parsha" ? "Weekly reading" : "Today's reading") : `${S.meta.sources} sources`}</span>
              <span className="chip"><Icon d="text" size={12} /> {weekly.ref ? weekly.sichaLabel || "This week" : `~${S.meta.readMins} min`}</span>
          <span className="mh-hero-go">Learn <Icon d="chev" size={14} /></span>
        </div>
      </button>

      {/* duo: hayom yom + live source map */}
      <div className="mh-duo">
        <div className="mh-card mh-hy">
          <div className="mh-card-tag"><Icon d="spark" size={12} /> Hayom Yom</div>
          <p lang="he" dir="rtl" className="heb mh-hy-he">{T.hayomyom.he}</p>
          <p className="mh-hy-en">{T.hayomyom.en}</p>
        </div>
        <button className="mh-card mh-map" onClick={() => { if (weekly.ref && window.loadSicha) window.loadSicha(weekly.ref, { recordActivity: false, syncUrl: false }).then(ok => { if (ok) { window.__mayanosExploreState = {}; onOpenExplore(); } }); else onOpenExplore(); }}>
          <div className="mh-card-tag"><Icon d="graph" size={12} /> Source map</div>
          <div className="mh-map-mini"><SourceGraph center={{collection: weekly.title || "Featured text"}} sources={featuredSources} height={270} animate={false} /></div>
          <span className="mh-map-go">{graphState.loading ? "Loading full-text connections…" : graphState.error ? "Open map to retry loading all connections" : `${featuredSources.length} sources · ${graphState.count} sections · Open full map`} <Icon d="chev" size={13} /></span>
        </button>
      </div>

      {/* continue — real activity from this browser */}
      {window.CONTINUE.length > 0 && <section className="mh-sec">
        <div className="mh-sec-head"><h3>Continue learning</h3></div>
        <div className="mh-cont">
          {window.CONTINUE.map((c) => (
            <button key={c.id} className="mh-cont-item" onClick={() => onOpenSicha(c.lastSegmentRef || c.ref)}>
              <ProgressRing value={c.progress} size={34} />
              <div className="mh-cont-txt">
                <span lang="he" dir="rtl" className="heb">{c.he}</span>
                <span className="mh-cont-en">{c.en}</span>
                <i>{c.coll}</i>
              </div>
              <Icon d="chev" size={16} />
            </button>
          ))}
        </div>
      </section>}

      {/* concepts */}
      {topicsVisible && <section className="mh-sec">
        <div className="mh-sec-head"><h3>Explore by concept</h3></div>
        <div className="mh-pills">
          {window.TOPICS.map((tp) => {
            const cid = mobileTopicConceptId(tp);
            return (
              <button key={tp.id} className="mh-pill" onClick={() => onOpenTopic && onOpenTopic(cid)}>
                <span lang="he" dir="rtl" className="heb">{tp.he}</span>
                <span className="mh-pill-en">{tp.en}</span>
              </button>
            );
          })}
        </div>
      </section>}

      {/* collections */}
      <section className="mh-sec">
        <div className="mh-sec-head"><h3>Featured collections</h3><button className="link-btn" onClick={onOpenLibrary}>Library <Icon d="chev" size={13} /></button></div>
        <div className="mh-coll">
          {window.COLLECTIONS.map((c) => (
            <button key={c.id} className="mh-coll-card" onClick={() => onOpenSicha && onOpenSicha(c.ref || c.en)}>
              <span lang="he" dir="rtl" className="heb mh-coll-he">{c.he}</span>
              <span className="mh-coll-en">{c.en}</span>
              <i>{c.author}</i>
              {(c.vols || c.sources) && <span className="mh-coll-meta"><Icon d="book" size={11} /> {[c.vols && `${c.vols} vol`, c.sources && `${c.sources} sources`].filter(Boolean).join(" · ")}</span>}
            </button>
          ))}
        </div>
      </section>
      <div style={{ height: 28 }} />
    </div>
  );
}

Object.assign(window, { MobileTabBar, MobileHome });
